import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { showError, showSuccess } from '@utils';
import { ebikesServices } from '@services';
import { usePermissions } from 'hooks/permissions';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import { handleEbikeData, handleEbikesBySku, handleEbikesEnums } from './utils';
import { useEbikeErrors } from './useEbikeErrors';
import { EBIKES_BY_SKU_PAGE_SIZE, INITIAL_EBIKE_VALUES } from './constants';

export const useEbikeOperations = () => {
  const queryClient = useQueryClient();

  const {
    push,
    query: { ebikeId, tab, page = 1, pageSize = EBIKES_BY_SKU_PAGE_SIZE },
  } = useRouter();
  const { findEntity } = usePermissions();
  const canSavePricing = findEntity([
    ENTITIES_PERMISIONS.WriteVehicleCatalogPricing,
  ]);

  const ebikeEnumsResponse = useQuery([QUERY_KEYS.GET_EBIKE_ENUMS_KEY], () =>
    ebikesServices.getEbikeEnums()
  );
  const ebikeProvidersResponse = useQuery(
    [QUERY_KEYS.GET_EBIKE_PROVIDERS_KEY],
    () => ebikesServices.getEbikeProviders()
  );
  const ebikeModelsResponse = useQuery([QUERY_KEYS.GET_EBIKE_MODELS_KEY], () =>
    ebikesServices.getEbikesModels()
  );
  const ebikesBySkuResponse = useQuery(
    [QUERY_KEYS.GET_EBIKES_BY_SKU_KEY, ebikeId, page, pageSize],
    () =>
      ebikesServices.getEbikesBySku(ebikeId, {
        page,
        pageSize,
      }),
    {
      enabled: !!ebikeId,
      keepPreviousData: true,
    }
  );

  // TODO: Remove the delay when we update the MUI to v7.3.6 or higher.
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms)); // eslint-disable-line no-promise-executor-return

  const ebikeResponse = useQuery(
    [QUERY_KEYS.GET_EBIKE_KEY, ebikeId],
    async () => {
      await delay(500);
      return ebikesServices.getEbike(ebikeId);
    },
    {
      enabled:
        !!ebikeId &&
        ebikeEnumsResponse.isFetched &&
        ebikeProvidersResponse.isFetched &&
        ebikeModelsResponse.isFetched &&
        ebikesBySkuResponse.isFetched,
    }
  );

  const postEbikeMutation = useMutation(
    async (payload) => {
      const { skuId } = payload.form;

      await ebikesServices.postEbike(payload.form);

      if (payload.pricing?.length && canSavePricing) {
        await Promise.all(
          payload.pricing.map((prices) => {
            if (prices?.id) {
              return ebikesServices.updatePricing(skuId, prices.id, prices);
            }
            return ebikesServices.addPricing(skuId, [prices]);
          })
        );
      }

      if (payload.docs?.length) {
        await payload.docs.reduce(async (prev, doc) => {
          await prev;
          return ebikesServices.postEbikeFile(skuId, doc.file);
        }, Promise.resolve());
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_EBIKE_KEY, ebikeId]);
        queryClient.invalidateQueries([QUERY_KEYS.GET_EBIKES_BY_SKU_KEY]);
        showSuccess('The ebike is created successfully!');
        setTimeout(() => {
          push(`${URLS.catalogEbikes}`);
        }, 1);
      },
      onError: (error) => {
        showError(error);
      },
    }
  );

  const editEbikeMutation = useMutation(
    async (payload) => {
      const { skuId } = payload.form;
      const { primaryImageId } = payload;

      const data = {
        ...payload.form,
        images: [...payload.images],
        documents: [...payload.docs],
      };

      await ebikesServices.updateEbike(skuId, data);

      if (payload.pricing?.length && canSavePricing) {
        await Promise.all(
          payload.pricing.map((prices) => {
            if (!prices?.insert) {
              return ebikesServices.updatePricing(skuId, prices.id, prices);
            }
            return ebikesServices.addPricing(skuId, [prices]);
          })
        );
      }

      if (primaryImageId) {
        await ebikesServices.postEbikePrimaryImage(skuId, primaryImageId);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_EBIKE_KEY, ebikeId]);
        showSuccess('The ebike is updated successfully!');
        setTimeout(() => {
          push(`${URLS.catalogEbikes}`);
        }, 1);
      },
      onError: showError,
    }
  );
  const deleteEbikeImageMutation = useMutation(
    async (payload) => {
      await ebikesServices.deleteEbikeImage(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_EBIKE_KEY, ebikeId]);
      },
      onError: showError,
    }
  );
  const deleteEbikeFileMutation = useMutation(
    async (payload) => {
      await ebikesServices.deleteEbikeFile(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.GET_EBIKE_KEY, ebikeId]);
      },
      onError: showError,
    }
  );

  useEbikeErrors({
    ebikeResponse,
    ebikeEnumsResponse,
    ebikeProvidersResponse,
    ebikeModelsResponse,
    ebikesBySkuResponse,
  });

  const refetch = useCallback(async () => {
    await ebikeResponse.refetch();
    await ebikesBySkuResponse.refetch();
  }, [ebikeResponse, ebikesBySkuResponse]);

  const isLoading =
    ebikeResponse.isLoading ||
    ebikeEnumsResponse.isLoading ||
    ebikeModelsResponse.isLoading ||
    ebikeProvidersResponse.isLoading ||
    ebikesBySkuResponse.isLoading;

  const ebike = useMemo(
    () =>
      handleEbikeData({
        ebike: ebikeResponse?.data?.data || INITIAL_EBIKE_VALUES,
        enums: ebikeEnumsResponse?.data?.data || {},
      }),
    [ebikeResponse?.data?.data, ebikeEnumsResponse?.data?.data]
  );
  const ebikesBySku = handleEbikesBySku(ebikesBySkuResponse?.data?.data || []);
  const enums = handleEbikesEnums(
    ebikeEnumsResponse?.data?.data || {},
    ebikeModelsResponse?.data?.data || {}
  );

  return {
    enums,
    loading: isLoading,
    ebike,
    ebikeId,
    ebikesBySku,
    ebikesBySkuTotal: ebikesBySkuResponse.data?.meta?.total || 0,
    ebikesBySkuPage: parseInt(page, 10),
    ebikesBySkuPageSize: pageSize || EBIKES_BY_SKU_PAGE_SIZE,
    ebikesBySkuIsLoading: ebikesBySkuResponse.isLoading,
    getEbikes: refetch,
    postEbike: postEbikeMutation.mutate,
    editEbike: editEbikeMutation.mutate,
    deleteEbikeImage: deleteEbikeImageMutation.mutate,
    deleteEbikeFile: deleteEbikeFileMutation.mutate,
    postLoading: postEbikeMutation.isLoading || editEbikeMutation.isLoading,
    refetch,
    tab,
  };
};
